import * as React from "react";
import { Checkbox, makeStyles } from "@fluentui/react-components";
import { FilterChipWrapper } from "./FilterChipWrapper";

type BooleanMode = "all" | "true" | "false";

const useStyles = makeStyles({
  list: {
    display: "flex",
    flexDirection: "column",
  },
});

export interface BooleanFilterProps {
  label: string;
  /** Called with `true`, `false`, or `null` (all / no filter). */
  onApply: (value: boolean | null) => void;
  /** Current applied value. */
  value?: boolean | null;
  /** Whether this filter has been modified from its default */
  isModified?: boolean;
  /** Called when the user clicks the reset icon */
  onReset?: () => void;
}

/**
 * Boolean filter — renders as a chip button with a popover containing
 * three checkbox options: All, True, False.
 */
export const BooleanFilter: React.FC<BooleanFilterProps> = ({
  label,
  onApply,
  value = null,
  isModified = false,
  onReset,
}) => {
  const styles = useStyles();

  const appliedMode: BooleanMode =
    value === true ? "true" : value === false ? "false" : "all";

  const [draft, setDraft] = React.useState<BooleanMode>(appliedMode);

  React.useEffect(() => {
    setDraft(appliedMode);
  }, [appliedMode]);

  function select(mode: BooleanMode) {
    setDraft(mode);
  }

  function apply() {
    if (draft === "all") {
      onApply(null);
    } else {
      onApply(draft === "true");
    }
  }

  function cancel() {
    setDraft(appliedMode);
  }

  const isDirty = draft !== appliedMode;

  const displayValue =
    appliedMode === "all" ? "All" : appliedMode === "true" ? "True" : "False";

  return (
    <FilterChipWrapper
      label={label}
      displayValue={displayValue}
      isModified={isModified}
      onReset={onReset ?? (() => {})}
      isDirty={isDirty}
      onApply={apply}
      onCancel={cancel}
      surfaceWidth="220px"
    >
      <div className={styles.list}>
        <Checkbox
          label="All"
          checked={draft === "all"}
          onChange={() => select("all")}
        />
        <Checkbox
          label="True"
          checked={draft === "true"}
          onChange={() => select("true")}
        />
        <Checkbox
          label="False"
          checked={draft === "false"}
          onChange={() => select("false")}
        />
      </div>
    </FilterChipWrapper>
  );
};